/**
 * The one way a score changes.
 *
 * Nothing outside this package's own appliers builds a new score by hand. Everything goes
 * through {@link apply}, which is what makes undo complete: a change that never produced
 * an inverse is a change the history does not know how to take back, and there is no way
 * to make one here.
 *
 * Failure is a value, never an exception. A command that names a bar which is no longer
 * there is an ordinary thing in an editor -- a stale selection, a queued suggestion, a
 * replayed edit -- and the caller gets a reason and the document it already had.
 */

import type { Score } from "../document/types";
import { done, fail } from "./helpers";
import type { Batch, Command, CommandResult } from "./types";
import {
  applyAddDirection,
  applyAddSpanner,
  applyInsertEvent,
  applyRemoveDirection,
  applyRemoveEvent,
  applyRemoveSpanner,
  applyReplaceEvent,
  applySetClef,
  applySetInstrument,
  applySetDuration,
  applySetEventProps,
  applySetPartMeasure,
  applySetPitches,
  applySetSpanners,
} from "./content";
import {
  applyInsertMeasure,
  applyInsertPart,
  applyRemoveMeasure,
  applyRemovePart,
  applyRestoreMeasure,
  applyRestorePart,
  applySetGroups,
  applySetLayout,
  applySetMeasure,
  applySetMeta,
  applySetPart,
} from "./timeline";

/**
 * All of them or none of them.
 *
 * A half-applied batch would leave a document no command describes and no inverse undoes,
 * so the score built along the way is thrown away the moment one step refuses. The inverse
 * runs the collected inverses backwards, which is the only order that puts things back:
 * undoing an insert before undoing what was written into it would have nothing to write.
 */
function applyBatch(score: Score, command: Batch): CommandResult {
  let current = score;
  const inverses: Command[] = [];

  for (const step of command.commands) {
    const result = apply(current, step);
    if (!result.ok) return fail(command, `${step.kind}: ${result.reason}`);
    current = result.score;
    inverses.unshift(result.inverse);
  }

  const inverse: Batch = { kind: "batch", commands: inverses };
  return done(
    current,
    command.label === undefined ? inverse : { ...inverse, label: command.label },
  );
}

/** Run a command against a score, returning the new score and the command that undoes it. */
export function apply(score: Score, command: Command): CommandResult {
  switch (command.kind) {
    case "setMeta":
      return applySetMeta(score, command);
    case "setLayout":
      return applySetLayout(score, command);
    case "setMeasure":
      return applySetMeasure(score, command);
    case "insertMeasure":
      return applyInsertMeasure(score, command);
    case "removeMeasure":
      return applyRemoveMeasure(score, command);
    case "restoreMeasure":
      return applyRestoreMeasure(score, command);
    case "insertPart":
      return applyInsertPart(score, command);
    case "removePart":
      return applyRemovePart(score, command);
    case "restorePart":
      return applyRestorePart(score, command);
    case "setPart":
      return applySetPart(score, command);
    case "setGroups":
      return applySetGroups(score, command);
    case "setPartMeasure":
      return applySetPartMeasure(score, command);
    case "setSpanners":
      return applySetSpanners(score, command);
    case "insertEvent":
      return applyInsertEvent(score, command);
    case "removeEvent":
      return applyRemoveEvent(score, command);
    case "replaceEvent":
      return applyReplaceEvent(score, command);
    case "setDuration":
      return applySetDuration(score, command);
    case "setPitches":
      return applySetPitches(score, command);
    case "setEventProps":
      return applySetEventProps(score, command);
    case "setClef":
      return applySetClef(score, command);
    case "setInstrument":
      return applySetInstrument(score, command);
    case "addDirection":
      return applyAddDirection(score, command);
    case "removeDirection":
      return applyRemoveDirection(score, command);
    case "addSpanner":
      return applyAddSpanner(score, command);
    case "removeSpanner":
      return applyRemoveSpanner(score, command);
    case "batch":
      return applyBatch(score, command);
  }
}

/** Run commands in order as one edit. Shorthand for a {@link Batch}. */
export function applyAll(
  score: Score,
  commands: readonly Command[],
  label?: string,
): CommandResult {
  const batch: Batch = { kind: "batch", commands };
  return apply(score, label === undefined ? batch : { ...batch, label });
}
